/**
 * InvoiceCard Component
 * Individual invoice summary card
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, FileText, Calendar, User, Truck } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/invoice.utils';
import type { InvoiceSummary } from '../types/invoice.types';

// ============================================
// STATUS BADGE CONFIG
// ============================================

const STATUS_CONFIG: Record<
  string,
  { color: string; bgColor: string; textColor: string }
> = {
  DRAFT: {
    color: 'bg-slate-100',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-800',
  },
  ISSUED: {
    color: 'bg-blue-100',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-800',
  },
  PAID: {
    color: 'bg-green-100',
    bgColor: 'bg-green-50',
    textColor: 'text-green-800',
  },
  PARTIALLY_PAID: {
    color: 'bg-amber-100',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-800',
  },
  OVERDUE: {
    color: 'bg-red-100',
    bgColor: 'bg-red-50',
    textColor: 'text-red-800',
  },
  CANCELLED: {
    color: 'bg-slate-100',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-800',
  },
};

// ============================================
// COMPONENT PROPS
// ============================================

export interface InvoiceCardProps {
  invoice: InvoiceSummary;
  onViewDetails: (invoiceId: number) => void;
  onDeliveryManagement?: (invoiceId: number) => void;
}

// ============================================
// COMPONENT
// ============================================

/**
 * InvoiceCard Component
 * Displays individual invoice summary with status and actions
 *
 * @example
 * ```tsx
 * <InvoiceCard
 *   invoice={invoice}
 *   onViewDetails={handleViewDetails}
 * />
 * ```
 */
export function InvoiceCard({
  invoice,
  onViewDetails,
  onDeliveryManagement,
}: InvoiceCardProps) {
  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.DRAFT;

  return (
    <Card
      className={`p-4 border-slate-200 transition-all hover:shadow-md cursor-pointer ${statusConfig.bgColor}`}
      onClick={() => onViewDetails(invoice.invoiceId)}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Section */}
        <div className="space-y-3">
          {/* Invoice Number & Date */}
          <div>
            <div className="flex items-start gap-2">
              <FileText className="h-5 w-5 text-slate-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">
                  {invoice.invoiceNumber}
                </h3>
                <p className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
                  <Calendar className="h-4 w-4" />
                  {formatDate(invoice.invoiceDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Customer & Amount */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-slate-600 flex-shrink-0" />
              <span className="text-slate-700 font-medium">
                {invoice.customerName}
              </span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-sm text-slate-600">Total Amount:</span>
              <span className="text-lg font-bold text-slate-900">
                {formatCurrency(invoice.totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex flex-col justify-between gap-4">
          {/* Status & Due Amount */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge className={`${statusConfig.color} ${statusConfig.textColor}`}>
                {invoice.status}
              </Badge>
              {invoice.dueDate && (
                <span className="text-xs text-slate-600">
                  Due: {formatDate(invoice.dueDate)}
                </span>
              )}
            </div>

            {/* Balance Due Info */}
            {invoice.balanceDue > 0 && (
              <div className="bg-white rounded p-2 border border-slate-200">
                <p className="text-xs text-slate-600">Balance Due</p>
                <p className="text-lg font-bold text-red-600">
                  {formatCurrency(invoice.balanceDue)}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              size="sm"
              className="flex-1 gap-2"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(invoice.invoiceId);
              }}
            >
              <Eye className="h-4 w-4" />
              View Details
            </Button>
            {onDeliveryManagement && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1 gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeliveryManagement(invoice.invoiceId);
                }}
              >
                <Truck className="h-4 w-4" />
                Delivery
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Layout - Stacked Below */}
      {invoice.notes && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <p className="text-xs text-slate-600">
            <span className="font-medium">Notes:</span> {invoice.notes.substring(0, 100)}
            {invoice.notes.length > 100 ? '...' : ''}
          </p>
        </div>
      )}
    </Card>
  );
}

export default InvoiceCard;
